// ** React Imports
import { useState, useEffect, useMemo } from "react";

// ** MUI Imports
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Grid,
  Collapse,
  Checkbox,
  Divider,
  useTheme
} from "@mui/material";

// ** Icon Imports
import Icon from "components/icon";

// ** Logic Imports
import { useSettingPermission } from "../permissions/SettingPermissions";

const ItemRow = ({ option, isChecked, onToggle, hasRoleSavePermission }) => {
  return (
    <>
      <Grid item xs={6}>
        <Box
          sx={{ display: "flex", alignItems: "center", height: "100%", pl: 5 }}
        >
          <Typography variant="body1">{option.action}</Typography>
        </Box>
      </Grid>
      <Grid item xs={6}>
        <Box sx={{ textAlign: "center" }}>
          <Checkbox 
            color="success" 
            checked={isChecked}
            disabled={!hasRoleSavePermission}
            {...(hasRoleSavePermission && {
              onChange: (e) => onToggle(e, option)
            })}
          />
        </Box>
      </Grid>
    </>
  );
};

const RolesnPerms = ({ entity, RoleProps }) => {
    //#region Permissions
    const { SettingPermissionProps } = useSettingPermission();
    const {
      // Role Permissions
      hasRoleSavePermission,
    } = SettingPermissionProps;
    //#endregion Permissions

  //#region States
  const theme = useTheme();
  const [openSections, setOpenSections] = useState({});
  const [selectedRoleName, setSelectedRoleName] = useState("");

  const {
    roles,
    selectedRole,
    configurationOptions,
    employeeOptions,
    customerOptions,
    mediaOptions,
    dispatchBoardOptions,
    serviceRequestOptions,
    invoiceOptions,
    dashboardOptions,
    settingsOptions,
    inventoryOptions,
    paymentOptions,
    profileOptions,
    servicesOptions,
    handleTogglePermission,
  } = RoleProps;

  //#endregion States

  const groupedOptions = useMemo(() => {
    let optionsArray = [];
    
    if (entity === "configuration") {
      optionsArray = Array.isArray(configurationOptions) ? configurationOptions : [];
    } else if (entity === "employee") {
      optionsArray = Array.isArray(employeeOptions) ? employeeOptions : [];
    } else if (entity === "customer") {
      optionsArray = Array.isArray(customerOptions) ? customerOptions : [];
    } else if (entity === "serviceRequest") {
      optionsArray = Array.isArray(serviceRequestOptions) ? serviceRequestOptions : [];
    } else if (entity === "attachments") {
      optionsArray = Array.isArray(mediaOptions) ? mediaOptions : [];
    } else if (entity === "dispatchBoard") {
      optionsArray = Array.isArray(dispatchBoardOptions) ? dispatchBoardOptions : [];
    } else if (entity === "invoice") {
      optionsArray = Array.isArray(invoiceOptions) ? invoiceOptions : [];
    } else if (entity === "dashboard") {
      optionsArray = Array.isArray(dashboardOptions) ? dashboardOptions : [];
    } else if (entity === "settings") {
      optionsArray = Array.isArray(settingsOptions) ? settingsOptions : [];
    } else if (entity === "inventory") {
      optionsArray = Array.isArray(inventoryOptions) ? inventoryOptions : [];
    } else if (entity === "payment") {
      optionsArray = Array.isArray(paymentOptions) ? paymentOptions : [];
    } else if (entity === "profile") {
      optionsArray = Array.isArray(profileOptions) ? profileOptions : [];
    } else if (entity === "services") {
      optionsArray = Array.isArray(servicesOptions) ? servicesOptions : [];
    }
  
    if (!Array.isArray(optionsArray)) {
      console.error('Expected optionsArray to be an array, got:', optionsArray);
      return [];
    }
  
    const grouped = optionsArray.reduce((acc, option) => {
      if (!acc[option.controller]) {
        acc[option.controller] = [];
      }
      acc[option.controller].push(option);
      return acc;
    }, {});
  
    return Object.entries(grouped).map(([controller, options]) => ({
      controller,
      options
    }));
  }, [entity, configurationOptions, employeeOptions, customerOptions, mediaOptions, serviceRequestOptions, invoiceOptions, dashboardOptions, settingsOptions]);

  useEffect(() => {
    if (selectedRole) {
      const selectedRoleObj = roles.find((role) => role.roleID === selectedRole);
      setSelectedRoleName(selectedRoleObj?.roleName || "");
    }
  }, [selectedRole, roles]);

  const handleClick = (controller) => {
    setOpenSections((prevState) => ({
      ...prevState,
      [controller]: !prevState[controller],
    }));
  };

  return (
    <Box>
      {entity === 'serviceRequest' && (
        <Box sx={{ border: '1px solid #E1E1E1', borderRadius: 1, p: 2, width: '100%', mb: 2 }}>
          <Typography variant='body2' color='text.secondary'>Note: Any changes within this tab will reflect in Requests, Assessments, Estimates and Work Orders tabs</Typography>
        </Box>
      )}
      <List component="nav" aria-label="main mailbox">
        {groupedOptions.map((item, index) => {
          const isOpen = openSections[item.controller] || false;

          return (
            <Box key={`${item.controller}-${index}`}>
              <ListItem
                disablePadding
                sx={{
                  backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : "#F4F4F4",
                  borderBottom: "1px solid #E1E4EA",
                  mb: 4,
                }}
              >
                <ListItemButton onClick={() => handleClick(item.controller)}>
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="body1" fontWeight="bold">
                          {item.controller}
                        </Typography>
                        <Icon
                          icon={
                            isOpen
                              ? "tabler:chevron-up"
                              : "tabler:chevron-down"
                          }
                        />
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>

              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <Grid container spacing={3} sx={{ py: 3 }}>
                  <Grid item xs={6}>
                    
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="body1" fontWeight="bold">
                        {selectedRoleName}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  {item.options.map((option) => (
                    <ItemRow 
                      key={option.permissionID} 
                      option={option}
                      isChecked={option.rolePermissionID !== 0 ? true : false}
                      onToggle={handleTogglePermission}
                      hasRoleSavePermission={hasRoleSavePermission}
                    />
                  ))}
                </Grid>
              </Collapse>
            </Box>
          );
        })}
      </List>
    </Box>
  );
};

export default RolesnPerms;